import React, { useEffect } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import Select from "react-select";
import cx from "classnames";

const SimpleValue = ({
  options,
  value,
  isMulti,
  optionValue,
  children,
  ...rest
}) => {
  const hasValue = value !== undefined && value !== null && value !== "";
  value =
    hasValue && !isMulti
      ? options.find((option) => option[optionValue] === value)
      : value;
  return children({ options, isMulti, value, ...rest });
};

const NewSelectS1 = (props) => {
  const {
    isClearable,
    isDisabled,
    disableOptions,
    className,
    label,
    isMulti,
    placeholder,
    options,
    field,
    styles,
    optionLabel,
    optionValue = "id",
    size,
    form: { touched, errors, setFieldValue, setFieldTouched },
    isSearchable,
    menuPlacement,
    initialValue,
    onClick,
    handleChange = () => {},
  } = props;

  const classes = cx(
    "form-field",
    touched[field.name] && errors[field.name] && "form-field--error",
    field.value !== undefined &&
      field.value !== null &&
      field.value !== "" &&
      "form-field--active",
    className,
  );

  const getSizeHeight = () => {
    switch (size) {
      case "default":
        return "48px";
      case "small":
        return "38px";
      case "very-small":
        return "30px";
      case "large":
        return "52px";
      default:
        return "48px";
    }
  };
  const getSizeRadius = () => {
    switch (size) {
      case "default":
        return "10px";
      case "small":
        return "5px";
      case "large":
        return "15px";
      default:
        return "10px";
    }
  };
  const getSizeFont = () => {
    switch (size) {
      case "default":
        return "16px";
      case "small":
        return "14px";
      case "very-small":
        return "10px";
      case "large":
        return "16px";
      default:
        return "16px";
    }
  };

  const customStyle = {
    container: (base) => ({
      ...base,
      minHeight: getSizeHeight(),
    }),
    control: (base, state) => ({
      ...base,
      cursor: "pointer",
      backgroundColor: "#ffffff",
      minHeight: getSizeHeight(),
      borderRadius: "12px",
      border:
        touched[field.name] && errors[field.name]
          ? "2px solid #ef4444"
          : state.isFocused
          ? "2px solid #60a5fa"
          : "2px solid #d1d5db",
      boxShadow: state.isFocused
        ? "0 0 0 4px rgba(96, 165, 250, 0.2)"
        : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      "&:hover": {
        borderColor: state.isFocused ? "#60a5fa" : "#93c5fd",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 16px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1f2937",
      fontSize: getSizeFont(),
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: getSizeFont(),
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: "#9ca3af",
      paddingRight: "12px",
      transition: "transform 0.3s ease",
      transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      marginTop: "8px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: getSizeFont(),
      padding: "12px 16px",
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#f3f4f6"
        : "transparent",
      color: state.isSelected ? "#ffffff" : "#374151",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#e5e7eb",
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#eff6ff",
      borderRadius: "8px",
      padding: "2px 8px",
      border: "1px solid #dbeafe",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1d4ed8",
      fontSize: "14px",
      fontWeight: "500",
      paddingRight: "4px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#3b82f6",
      cursor: "pointer",
      borderRadius: "0 6px 6px 0",
      "&:hover": {
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
      },
    }),
  };

  useEffect(() => {
    if (initialValue) {
      setFieldValue(field.name, initialValue);
    }
  }, []);

  return (
    <div className={classes}>
      {label && <div className="form-field__label">{label}</div>}
      <SimpleValue
        id={field.name}
        name={field.name}
        onChange={(option) => {
          if (isMulti) {
            setFieldValue(field.name, option);
          } else {
            setFieldValue(field.name, get(option, optionValue, undefined));
          }
          handleChange(option);
        }}
        onClick={onClick}
        onBlur={() => setFieldTouched(field.name, true)}
        options={options}
        optionValue={optionValue}
        getValue={(option) => option[optionValue]}
        getOptionLabel={(option) => option[optionLabel]}
        getOptionValue={(option) => option[optionValue]}
        placeholder={placeholder}
        styles={styles || customStyle}
        value={field.value}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        menuPlacement={menuPlacement}
        isOptionDisabled={(option) =>
          disableOptions
            .reduce((prev, curr) => [...prev, curr.id], [])
            .includes(option.id)
        }
        components={{
          DropdownIndicator: () => (
            <div
              style={{
                paddingRight: "12px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.8 }}
              >
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          ),
        }}
      >
        {(simpleProps) => <Select {...simpleProps} />}
      </SimpleValue>
      {touched[field.name] && errors[field.name] && (
        <div className="form-field__error">
          <span>{errors[field.name]}</span>
        </div>
      )}
    </div>
  );
};

NewSelectS1.propTypes = {
  label: PropTypes.string.isRequired,
  optionLabel: PropTypes.string,
  optionValue: PropTypes.string,
  options: PropTypes.array.isRequired,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  menuPlacement: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(["default", "large", "small", "very-small"]),
};

NewSelectS1.defaultProps = {
  optionLabel: "name",
  optionValue: "id",
  isSearchable: false,
  isClearable: false,
  menuPlacement: "bottom",
  className: "",
  label: "",
  disableOptions: [],
  size: "default",
};

export default NewSelectS1;
